import { HTMLAttributes, ReactNode, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * Pill — DESIGN_SYSTEM.md §2.1
 * Larger composable chip: language pill, setup-complete pill, booking-page
 * chip, professional filter pill, eyebrow.
 *
 * Standard: 6-8px / 12px padding, radius-pill, 12-13px / 500-600.
 */

const pillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-pill border transition-colors text-[13px] font-medium',
  {
    variants: {
      variant: {
        default:  'bg-surface border-line text-ink hover:border-brand-300',
        selected: 'bg-brand-50 border-brand-500 text-brand-700',
        ghost:    'bg-transparent border-transparent text-soft hover:bg-surface-2',
        eyebrow:  'bg-brand-100 border-transparent text-brand-700 text-[12px] font-semibold tracking-wide',
      },
      size: {
        sm: 'px-2.5 py-1 text-[12px]',
        md: 'px-3 py-1.5',
        lg: 'px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface PillProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof pillVariants> {
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Pill = forwardRef<HTMLSpanElement, PillProps>(
  ({ className, variant, size, leading, trailing, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(pillVariants({ variant, size }), className)}
      {...props}
    >
      {leading && <span className="inline-flex items-center [&_svg]:h-3.5 [&_svg]:w-3.5">{leading}</span>}
      {children}
      {trailing && <span className="inline-flex items-center [&_svg]:h-3.5 [&_svg]:w-3.5">{trailing}</span>}
    </span>
  ),
);
Pill.displayName = 'Pill';

export { pillVariants };
